import React, { useEffect, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { useLang } from "@/i18n";
import { Save, Award, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { key: "registered" },
  { key: "email_verified" },
  { key: "completed_questionnaire" },
  { key: "pilot_interest" },
  { key: "referral" },
];

const AdminSettings = () => {
  const { t } = useLang();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/settings/rewards").then((r) => setCfg(r.data)); }, []);

  const update = (k, v) => setCfg((c) => ({ ...c, [k]: Math.max(0, Number(v) || 0) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const f of FIELDS) payload[f.key] = cfg[f.key];
      const { data } = await api.patch("/admin/settings/rewards", payload);
      setCfg(data);
      toast.success(t("common.saved"));
    } catch (e) { toast.error(extractErrorMessage(e)); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div>{t("common.loading")}</div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="admin-settings">
      <div>
        <div className="tsw-overline">{t("admin.title")}</div>
        <h1 className="font-display font-bold text-3xl text-slate-900 flex items-center gap-2">
          <Award className="w-7 h-7 text-emerald-600" /> {t("phase2.rewardSystemHeading")}
        </h1>
        <p className="text-slate-600 mt-2 text-sm">
          {t("phase2.rewardsConfigSub")}
        </p>
      </div>

      <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-4 text-sm text-emerald-900 flex items-start gap-2" data-testid="rewards-policy-banner">
        <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5" />
        <div>
          {t("phase2.rewardsPolicyBanner")}
        </div>
      </div>

      <div className="tsw-card p-6 space-y-4">
        {FIELDS.map((f) => (
          <div key={f.key} className="grid sm:grid-cols-3 items-center gap-3 border-b border-slate-100 pb-3 last:border-0">
            <div className="sm:col-span-2 font-semibold text-slate-900">
              {t(`rewards.${f.key}`)}
            </div>
            <input
              type="number"
              min={0}
              className="tsw-input"
              value={cfg[f.key] ?? 0}
              onChange={(e) => update(f.key, e.target.value)}
              data-testid={`rewards-${f.key}`}
            />
          </div>
        ))}
        <div className="flex justify-end">
          <button onClick={save} disabled={saving} className="tsw-btn-primary" data-testid="rewards-save">
            <Save className="w-4 h-4" /> {saving ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
